/* eslint-disable react-hooks/exhaustive-deps */
import LoadingContent from 'components/LoadingContent';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import React, { useEffect } from 'react';
import TopProcessBar from 'screen/selectCollege/TopProcessBar';
import { Container, Form, Spinner, Button } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { useHistory, useLocation } from 'react-router-dom';
import { routers } from 'router/router';
import { logout } from 'app/basicInformationSlice/basicInformationSlice';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import SearchIcon from '@mui/icons-material/Search';
import YesNoSwitch from 'components/input/YesNoSwitch';
import SelectInput from 'components/input/SelectInput';
import DatePickerInput from 'components/input/DatePicker';
import {
  checkEnableParseJSON,
  formatBoolean,
  optionValues,
  setArrayLength,
} from 'components/helper/helper';
import { getSATTest, updateSATTest } from 'app/testingSlice/testingSlice';
import { debounce } from 'lodash';
import TextInput from 'components/input/textInput';
import SATItem from './SATItem';
import Grid from '@mui/joy/Grid';
import { ModalCheckTestFields } from '../common/ModalCheckTestFields';
import pageStatusService from 'services/pageStatusService';
import { PAGE_NAME } from 'constants/common';
import { toggleReviewedAndComplete } from 'services/reviewAndComplete';
import { getAccessToken, getUserId, removeAccessToken, removeUserId } from 'utils/sessionStorage';
// import Cookies from 'js-cookie';
// import { authenticateSettings } from 'components/helper/appConfig';


const SATSampleItem = {
  test_date: '',
  total_score: '',
  reading_writing_score: '',
  math_score: '',
  essay_scores: '',
  essay_reading: '',
  essay_analysis: '',
  essay_writing: '',
};

const createValidationSchema = () => {
  return Yup.object().shape({
    csu_info: Yup.object().shape({
      sat_score: Yup.array().of(
        Yup.object().shape({
          test_date: Yup.string().nullable()
            .required('Test date is required')
            .test(
              'is-valid-date',
              'Invalid date',
              function (value: any) {
                if (!value) {
                  return false;
                }

                let year = moment(value).format('yyyy');
                if (Number(year) < 2000) return false;

                return moment(value).isValid();
              }
            ),
        })
      )
    }),
    future_testing_date_1: Yup.object().shape({
      test1: Yup.string().nullable(),
      test2: Yup.string().nullable(), 
      test3: Yup.string().nullable(),
    })
  });
};

const SATTest = () => {
  const [loading, setLoading] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth <= 768);
  const testing:any = useAppSelector((store) => store.testing);
  const dispatch:any = useAppDispatch();
  const history:any = useHistory();
  let studentId = getUserId();
  let token = getAccessToken();
  const query = new URLSearchParams(useLocation().search);
  const [check, setCheck] = React.useState(query.get('check'));
  const [showModal, setShowModal] = React.useState(false);
  const [testData, setTestData] = React.useState<any>(null);
  const [reviewedAndComplete, setReviewedAndComplete] = React.useState(false);
  const onReviewdAndComplete = () => {
    void toggleReviewedAndComplete(PAGE_NAME.SAT_TEST, reviewedAndComplete, setReviewedAndComplete);
  }
  const formik = useFormik({
    initialValues: {
      is_have_sat_scores_report: true,
      is_took_before_march_2016: true,
      is_took_after_march_2016: true,
      month: '',
      year: 0,
      times_taken_sat: '',
      number_of_past_sat_scores: 0,
      highest_total_score: 0,
      test_date: '',
      highest_evidence_reading_writing_score: 0,
      evidence_reading_writing_score: 0,
      evidence_reading_writing_date: '',
      highest_math_score_based_evidence_reading_writing_score: 0,
      math_score: 0,
      math_reading_writing_date: '',
      combined_essay_test_date: '',
      combined_essay_reading_score: 0,
      combined_essay_reading_score_sub: 0,
      combined_essay_analysis_score: 0,
      combined_essay_analysis_score_sub: 0,
      combined_essay_writing_score: 0,
      combined_essay_writing_score_sub: 0,
      future_sat_tests_plan_to_take: 0,
      future_testing_date_1: {
        test1: '',
        test2: '',
        test3: '',
      },
      test_score_math: 0,
      test_score_reading: 0,
      test_score_writing_and_language: 0,

      taken_sat_essay_option: '',
      combined_essay_score: null as number | null,
      combined_essay_date: '',
      csu_info: {
        collegeBoardId: '',
        sat_score: [SATSampleItem],
      },
    },
    validationSchema: createValidationSchema(),
    validate: (values) => {
      const errors: any = {};
      
      // Custom validation for future testing dates
      const futureTestPlan = values.future_sat_tests_plan_to_take;
      
      // Validate test1
      if ([1, '1', '2', '3', 2, 3].includes(futureTestPlan)) {
        const test1Value = values.future_testing_date_1?.test1;
        if (!test1Value) {
          if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
          errors.future_testing_date_1.test1 = 'Future testing date is required';
        } else {
          if (!moment(test1Value).isValid()) {
            if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
            errors.future_testing_date_1.test1 = 'Invalid date';
          } else {
            const year = moment(test1Value).year();
            if (year.toString().length !== 4) {
              if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
              errors.future_testing_date_1.test1 = 'Invalid date';
            }
          }
        }
      }
      
      // Validate test2
      if (['2', '3', 2, 3].includes(futureTestPlan)) {
        const test2Value = values.future_testing_date_1?.test2;
        if (!test2Value) {
          if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
          errors.future_testing_date_1.test2 = 'Future testing date is required';
        } else {
          if (!moment(test2Value).isValid()) {
            if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
            errors.future_testing_date_1.test2 = 'Invalid date';
          } else {
            const year = moment(test2Value).year();
            if (year.toString().length !== 4) {
              if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
              errors.future_testing_date_1.test2 = 'Invalid date';
            }
          }
        }
      }
      
      // Validate test3
      if (['3', 3].includes(futureTestPlan)) {
        const test3Value = values.future_testing_date_1?.test3;
        if (!test3Value) {
          if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
          errors.future_testing_date_1.test3 = 'Future testing date is required';
        } else {
          if (!moment(test3Value).isValid()) {
            if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
            errors.future_testing_date_1.test3 = 'Invalid date';
          } else {
            const year = moment(test3Value).year();
            if (year.toString().length !== 4) {
              if (!errors.future_testing_date_1) errors.future_testing_date_1 = {};
              errors.future_testing_date_1.test3 = 'Invalid date';
            }
          }
        }
      }
      
      return errors;
    },
    onSubmit: async (values) => {
      try {
        let number_of_past_sat_scores = values.number_of_past_sat_scores
          ? parseInt(`${values.number_of_past_sat_scores}`)
          : 0;
        let newValues: any = {
          ...values,
          student_id: studentId,
          csu_info: JSON.stringify(values.csu_info),
          number_of_past_sat_scores: `${values.number_of_past_sat_scores}`,
          is_have_sat_scores_report: number_of_past_sat_scores > 0,
          future_testing_date_1: JSON.stringify(values.future_testing_date_1),
        };
        await dispatch(updateSATTest(newValues));
      } catch (ex) {
        console.log('Error: ', ex);
      }
    },
  });

  const handleLogout = () => {
    dispatch(logout());
    removeAccessToken();
    removeUserId();
    history.push(routers.login.path);
  };

  const handleContinue = () => {
    formik.handleSubmit();
    const nextRoute = Object.keys(testing.roleTest).find(
      (key, index) =>
        index > Object.keys(testing.roleTest).indexOf('satTest') && testing.roleTest[key] === true
    );
    history.push(nextRoute ? routers?.[nextRoute]?.path : routers.activities.path);
  };

  const getDetail = async () => {
    studentId = getUserId();
    token = getAccessToken();
    try {
      if (token && studentId) {
        setLoading(true);
        const pageStatus: {
          student_completed: boolean;
        } = await pageStatusService.getPageStatusByPageName(studentId, PAGE_NAME.SAT_TEST);
        if(pageStatus) {
          setReviewedAndComplete(pageStatus.student_completed);
        }
        let res:any = await dispatch(getSATTest(studentId));
        const dataLoaded: any = res.payload;
        if (dataLoaded) {
          let values: any = {
            ...dataLoaded,
            number_of_past_sat_scores: dataLoaded.number_of_past_sat_scores
              ? parseInt(dataLoaded.number_of_past_sat_scores)
              : 0,
            csu_info: {
              ...formik.values.csu_info,
              ...checkEnableParseJSON(dataLoaded.csu_info),
            },
            future_testing_date_1: {
              ...formik.values.future_testing_date_1,
              ...checkEnableParseJSON(dataLoaded.future_testing_date_1),
            },
          };

          formik.resetForm({ values: values });
          setTestData(values);
        }
        setLoading(false);
        formik.setStatus('idle');
      } else {
        handleLogout();
      }
    } catch (ex) {
      console.log('Error: ', ex);
    }
  };

  useEffect(() => {
    getDetail();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (formik.dirty && formatBoolean(formik.values.is_have_sat_scores_report) === false) {
      formik.setValues({
        is_took_before_march_2016: true,
        is_took_after_march_2016: true,
        month: '',
        year: 0,
        times_taken_sat: '',
        number_of_past_sat_scores: 0,
        highest_total_score: 0,
        test_date: '',
        highest_evidence_reading_writing_score: 0,
        evidence_reading_writing_score: 0,
        evidence_reading_writing_date: '',
        highest_math_score_based_evidence_reading_writing_score: 0,
        math_score: 0,
        math_reading_writing_date: '',
        combined_essay_test_date: '',
        combined_essay_reading_score: 0,
        combined_essay_reading_score_sub: 0,
        combined_essay_analysis_score: 0,
        combined_essay_analysis_score_sub: 0,
        combined_essay_writing_score: 0,
        combined_essay_writing_score_sub: 0,
        future_sat_tests_plan_to_take: 0,
        future_testing_date_1: {
          test1: '',
          test2: '',
          test3: '',
        },
        test_score_math: 0,
        test_score_reading: 0,
        test_score_writing_and_language: 0,
        is_have_sat_scores_report: false,
        taken_sat_essay_option: '',
        combined_essay_score: null,
        combined_essay_date: '',
        csu_info: {
          collegeBoardId: '',
          sat_score: [SATSampleItem],
        },
      });
    }
  }, [formik.values.is_have_sat_scores_report]);

  useEffect(() => {
    const readingWritingScore = getHighestScore('reading_writing_score');
    const mathScore = getHighestScore('math_score');
    const essayScore = getHighestScore('essay_scores');

    formik.setValues(
      {
        ...formik.values,
        highest_evidence_reading_writing_score: readingWritingScore.max,
        evidence_reading_writing_date: readingWritingScore.date,
        highest_math_score_based_evidence_reading_writing_score: mathScore.max,
        math_reading_writing_date: mathScore.date,
        combined_essay_score: essayScore.max,
        combined_essay_date: essayScore.date,
      },
      false
    );
  }, [formik.values.csu_info?.sat_score]);

  const getHighestScore = (type: string) => {
    const result = { max: 0, date: '' };

    (formik.values.csu_info?.sat_score ?? []).forEach((item: any) => {
      switch (type) {
        case 'essay_scores':
          if (Math.max(result.max, parseInt(item.essay_scores)) === parseInt(item.essay_scores)) {
            result.max = parseInt(item.essay_scores);
            result.date = item.test_date;
          }
          break;
        case 'reading_writing_score':
          if (
            Math.max(result.max, parseInt(item.reading_writing_score)) ===
            parseInt(item.reading_writing_score)
          ) {
            result.max = parseInt(item.reading_writing_score);
            result.date = item.test_date;
          }
          break;
        case 'math_score':
          if (Math.max(result.max, parseInt(item.math_score)) === parseInt(item.math_score)) {
            result.max = parseInt(item.math_score);
            result.date = item.test_date;
          }
          break;
        default:
          break;
      }
    });

    return result;
  };

  const syncTestData = () => {
    try {
      setTestData(formik.values);
    } catch (ex) {
      console.log('Error: ', ex);
    }
  }

  // Auto save function ==========
  const notInitialRender = React.useRef(false);
  React.useEffect(() => {
    if (formik.dirty) {
      debounceSaving();
      syncTestData();
    }

  }, [formik.values]);
  const debounceSaving = React.useCallback(
    debounce(() => {
      if (notInitialRender.current) {
        if (!loading) formik.handleSubmit();
      } else {
        notInitialRender.current = true;
      }
    }, 1500),
    []
  );
  // Auto save function ==========

  const number_of_past_sat_scores_options = [
    { value: 0 },
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
  ];

  const handleCheckMissing = () => {
    setCheck('1');
    syncTestData();
    setShowModal(true);
  }

  const handleCloseModal = () => setShowModal(false);

  return loading ? (
    <LoadingContent />
  ) : (
    <>
      <TopProcessBar
        stepProcess={[
          { title: 'General', percentOnProcess: 25 },
          { title: 'Reading/Writing', percentOnProcess: 40 },
          { title: 'Math', percentOnProcess: 60 },
          { title: 'Future SAT', percentOnProcess: 80 },
        ]}
        title="Standardized Testing"
        process={1}
        icon={<CalculateOutlinedIcon />}
        isMobile={isMobile}
      />

      <Container 
        className={`main-container gm-basic-info-page ${isMobile ? 'p-mobile-2' : ''}`}
        style={{
          paddingLeft: isMobile ? '12px' : undefined,
          paddingRight: isMobile ? '12px' : undefined,
          paddingTop: isMobile ? '120px' : undefined,
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <div className={`gm-section-card ${isMobile ? 'p-mobile-2' : 'px-4 py-4'}`}>
          <Form
            style={{ 
              maxWidth: isMobile ? '100%' : '900px',
              width: '100%'
            }}
            onSubmit={formik.handleSubmit}
            className="gm-form-grid"
          >
            <div className={`gm-section-title ${isMobile ? 'mb-2' : 'mb-4'}`}>
              <span style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: 600 }}>
                SAT Tests
              </span>
            </div>
            <Grid container spacing={isMobile ? 1 : 2} className="gm-form-fields">
              <Grid xs={12}>
                <div className="my-2">Let's put all your SAT testing information in one place!</div>
              </Grid>

              <Grid xs={isMobile ? 12 : 6}>
                <SelectInput
                  logo="span-cm-logo"
                  form={formik}
                  option={number_of_past_sat_scores_options}
                  label="Number of past SAT scores you wish to report*"
                  dataUp="sat-number-scores"
                  name="number_of_past_sat_scores"
                  isSatTest={true}
                  onChange={(e) => {
                    let newValues = Object.assign({}, formik.values);
                    newValues.number_of_past_sat_scores = e.target.value;
                    newValues.csu_info.sat_score = setArrayLength(
                          newValues.csu_info?.sat_score ?? [],
                          e.target.value,
                          SATSampleItem
                        );
                    formik.setValues(newValues);
                  }}
                  values={formik.values.number_of_past_sat_scores}
                  isMissing={formik.values.number_of_past_sat_scores === null && check}
                  disabled={reviewedAndComplete}
                />
              </Grid>

              <Grid xs={isMobile ? 12 : 6}>
                <TextInput
                  name="csu_info.collegeBoardId"
                  onChange={formik.handleChange}
                  disabled={reviewedAndComplete}
                  values={formik.values.csu_info?.collegeBoardId}
                  type="number"
                  label="College Board ID"
                  logo="span-csu-logo"
                  countText={9}
                />
              </Grid>

              <Grid xs={12}>
                <YesNoSwitch
                  id="taken_sat_essay_option"
                  className="mb-3"
                  label="Have you taken the SAT essay?*"
                  name="taken_sat_essay_option"
                  disabled={reviewedAndComplete}
                  values={formik.values.taken_sat_essay_option}
                  onChange={formik.handleChange}
                  logo="span-cm-logo"
                  isMissing={!formik.values.taken_sat_essay_option && check}
                />
              </Grid>

              <div className="w-100 bg-white py-2 px-2 rounded-3">
                {(formik.values.csu_info?.sat_score ?? []).map((item, index) => (
                  <Grid xs={12} key={index}>
                    <SATItem form={formik} index={index} check={check} isMobile={isMobile} disabled={reviewedAndComplete} />
                  </Grid>
                ))}
              </div>

              <Grid xs={12}>
                <div className="text-field-light my-3"></div>
              </Grid>

              <Grid xs={12}>
                <div className={`${isMobile ? 'fs-6' : 'fs-5'} p-1 span-cm-logo mt-3`} id="ReadingWriting">
                  Super Score
                </div>
              </Grid>

              <Grid xs={6}>
                <TextInput
                  name="highest_evidence_reading_writing_score"
                  label="Highest Reading/Writing Score*"
                  disabled={true || reviewedAndComplete}
                  values={getHighestScore('reading_writing_score').max}
                  onChange={(e) => formik.setFieldValue('highest_evidence_reading_writing_score', e.target.value)}
                  // isMissing={!formik.values.highest_evidence_reading_writing_score && check}
                />
              </Grid>

              <Grid xs={6}>
                <DatePickerInput
                  className="common-coallition-flag end-2-flag"
                  label="Highest Reading/Writing Score Date*"
                  name="evidence_reading_writing_date"
                  disabled={true || reviewedAndComplete}
                  values={getHighestScore('reading_writing_score').date}
                  onChange={(values) => formik.setFieldValue('evidence_reading_writing_date', values)}
                  // isMissing={!formik.values.evidence_reading_writing_date && check}
                />
              </Grid>

              <Grid xs={6}>
                <TextInput 
                 label="Highest Math Score*"
                 name="highest_math_score_based_evidence_reading_writing_score"
                 disabled={true || reviewedAndComplete}
                 values={getHighestScore('math_score').max}
                 onChange={(e) => formik.setFieldValue('highest_math_score_based_evidence_reading_writing_score', e.target.value)}
                //  isMissing={!formik.values.highest_math_score_based_evidence_reading_writing_score && check}
                />
              </Grid>

              <Grid xs={6}>
                <DatePickerInput
                  label="Highest Math Score Date*"
                  name="math_reading_writing_date"
                  disabled={true}
                  values={getHighestScore('math_score').date}
                  onChange={(values) => formik.setFieldValue('math_reading_writing_date', values)}
                  // isMissing={!formik.values.math_reading_writing_date && check}
                />
              </Grid>

              {formik.values.taken_sat_essay_option === 'true' && (
                <>
                  <Grid xs={6}>
                    <TextInput
                      name="highest_math_score_based_evidence_reading_writing_score"
                      label="Highest Combined Essay Score*"
                      disabled={true}
                      values={getHighestScore('essay_scores').max}
                      onChange={(e) => formik.setFieldValue('combined_essay_score', e.target.value)}
                      // isMissing={!formik.values.combined_essay_score && check}

                    />
                  </Grid>

                  <Grid xs={6}>
                    <DatePickerInput
                      className="common-coallition-flag end-2-flag"
                      label="Highest Combined Essay Score Date*"
                      name="combined_essay_date"
                      disabled={true}
                      values={getHighestScore('essay_scores').date}
                      onChange={(values) => formik.setFieldValue('combined_essay_date', values)}
                      // isMissing={!formik.values.combined_essay_date && check}
                    />
                  </Grid>
                </>
              )}

              <Grid xs={12}>
                <div className="text-field-light my-3"></div>
              </Grid>

              <Grid xs={12}>
                <div className={`my-3 ${isMobile ? 'fs-6' : 'fs-5'} p-1`} id="FutureSAT">
                  Future SATs
                </div>
              </Grid>

              <Grid xs={12}>
                <SelectInput
                  className="common-flag"
                  form={formik}
                  option={optionValues(Array.from(Array(4).keys()))}
                  label="Number of future SAT sittings you expect*" //"Future SAT Tests You Plan To Take?"
                  name="future_sat_tests_plan_to_take"
                  values={formik.values.future_sat_tests_plan_to_take}
                  onChange={formik.handleChange}
                  isMissing={formik.values.future_sat_tests_plan_to_take === null && check}
                  disabled={reviewedAndComplete}
                />
              </Grid>

              {[1, '1', '2', '3', 2, 3].includes(formik.values.future_sat_tests_plan_to_take) && (
                <Grid xs={6}>
                  <>
                    <DatePickerInput
                      className="common-flag"
                      label={`Future testing date 1*`}
                      name="future_testing_date_1.test1"
                      values={formik.values?.future_testing_date_1?.test1}
                      onChange={(value) => formik.setFieldValue('future_testing_date_1.test1', value)}
                      isMissing={!formik.values?.future_testing_date_1?.test1 && check}
                      disabled={reviewedAndComplete}
                    />
                    {formik.errors?.future_testing_date_1?.test1 && 
                     formik.touched?.future_testing_date_1?.test1 && (
                      <div className="text-danger">{formik.errors.future_testing_date_1.test1}</div>
                    )}
                  </>
                </Grid>
              )}

              {['2', '3', 2, 3].includes(formik.values.future_sat_tests_plan_to_take) && (
                <Grid xs={6}>
                  <>
                    <DatePickerInput
                      className="common-flag end-flag"
                      label={`Future testing date 2*`}
                      name="future_testing_date_1.test2"
                      values={formik.values?.future_testing_date_1?.test2}
                      onChange={(value) => formik.setFieldValue('future_testing_date_1.test2', value)}
                      isMissing={!formik.values?.future_testing_date_1?.test2 && check}
                      disabled={reviewedAndComplete}
                    />
                    {formik.errors?.future_testing_date_1?.test2 && 
                     formik.touched?.future_testing_date_1?.test2 && (
                      <div className="text-danger">{formik.errors.future_testing_date_1.test2}</div>
                    )}
                  </>
                </Grid>
              )}

              {['3', 3].includes(formik.values.future_sat_tests_plan_to_take) && (
                <Grid xs={6}>
                  <>
                    <DatePickerInput
                      className="common-flag"
                      label={`Future testing date 3*`}
                      name="future_testing_date_1.test3"
                      values={formik.values?.future_testing_date_1?.test3}
                      onChange={(value) => formik.setFieldValue('future_testing_date_1.test3', value)}
                      isMissing={!formik.values?.future_testing_date_1?.test3 && check}
                      disabled={reviewedAndComplete}
                    />
                    {formik.errors?.future_testing_date_1?.test3 && 
                     formik.touched?.future_testing_date_1?.test3 && (
                      <div className="text-danger">{formik.errors.future_testing_date_1.test3}</div>
                    )}
                  </>
                </Grid>
              )}
            </Grid>
            <div className="row my-3 pt-3 mt-4 border-top">
              <div className="col-md-6">
                <div className="text-left pt-3">
                  <div className="gm-btn-checkmissing"
                    onClick={() => handleCheckMissing()}
                  >
                    <SearchIcon/><span>Check missing values</span>
                  </div>
                  <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                    <input
                      type="checkbox"
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #6c757d',
                        borderRadius: '2px',
                      }}  
                      checked={reviewedAndComplete}
                      onClick={onReviewdAndComplete}
                    />
                    <span
                      style={{
                        fontSize: isMobile ? '14px' : '12px',
                        color: '#6c757d',
                        fontWeight: '500',
                      }}
                    >
                      Reviewed
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-start text-md-end">
                <Button 
                    variant="primary" 
                    type="submit" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5 mx-2'} gm-btn-save`}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    {testing.status === 'loading' ? (
                      <Spinner size="sm" animation="border" variant="light" />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  <Button 
                    variant="primary" 
                    className={`${isMobile ? 'w-100 py-3' : 'p-2 px-5'} gm-btn-continue`}
                    onClick={() => handleContinue()}
                    style={{
                      fontSize: isMobile ? '16px' : '14px',
                      minHeight: isMobile ? '44px' : 'auto'
                    }}
                  >
                    Continue
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        </div>
        <ModalCheckTestFields
          show={showModal}
          onClose={handleCloseModal}
          data={testData}
          page="sat-test"
        />
      </Container>
    </>
  );
};

export default SATTest;
